import {
  contextLinkingPopoverAtom,
  linkedContextOriginFor,
  stageLinkedPatientAtom,
  stageLinkedSessionAtom,
  type ContextLinkingPopoverType,
  type LinkedPatientContext,
  type LinkedSessionContext,
} from '@/atoms/chatAtoms';
import type { Editor } from '@tiptap/react';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

interface UseContextLinkingAutocompleteProps {
  editor: Editor | null;
  enabled: boolean;
  /** Whether context linking is locked (chat has started) */
  isContextLocked?: boolean;
  /**
   * The chat the selection is staged for; null/undefined on a new-chat
   * surface. Recorded as the linked-context origin so the send-boundary
   * provenance gate (resolveAttachableLinkedContext) can tell a deliberate
   * inline link apart from context primed by another chat's backend-sync.
   */
  chatId?: string | null;
}

interface UseContextLinkingAutocompleteReturn {
  /** Whether the autocomplete popover is open */
  isOpen: boolean;
  /** The type of autocomplete showing ('session' for #, 'patient' for @) */
  type: ContextLinkingPopoverType;
  /** The search query after the trigger character */
  searchQuery: string;
  /** Called when user selects a session */
  selectSession: (session: LinkedSessionContext) => void;
  /** Called when user selects a patient */
  selectPatient: (patient: LinkedPatientContext) => void;
  /** Called to close the autocomplete */
  close: () => void;
}

/**
 * Hook to handle inline autocomplete for "#" (sessions) and "@" (patients)
 * Similar to Slack/Linear/Cursor command menus
 */
export function useContextLinkingAutocomplete({
  editor,
  enabled,
  isContextLocked = false,
  chatId,
}: UseContextLinkingAutocompleteProps): UseContextLinkingAutocompleteReturn {
  const [popoverType, setPopoverType] = useAtom(contextLinkingPopoverAtom);
  const stageLinkedSession = useSetAtom(stageLinkedSessionAtom);
  const stageLinkedPatient = useSetAtom(stageLinkedPatientAtom);
  const [searchQuery, setSearchQuery] = useState('');
  const [triggerIndex, setTriggerIndex] = useState<number | null>(null);

  const isOpen = popoverType !== null;

  // Detect trigger characters and track search query
  useEffect(() => {
    // Don't enable autocomplete if context is locked (chat has started)
    if (!editor || !enabled || isContextLocked) return;

    const handleUpdate = () => {
      const text = editor.getText();
      const { from } = editor.state.selection;

      // Look for trigger character (# or @) before cursor
      // Find the last trigger that's either at start or after whitespace
      let lastTriggerIndex = -1;
      let triggerChar: '#' | '@' | null = null;

      for (let i = from - 1; i >= 0; i--) {
        const char = text[i];

        // Stop if we hit whitespace before finding a trigger
        if (char === ' ' || char === '\n') {
          break;
        }

        if (char === '#' || char === '@') {
          // Check if it's at start or after whitespace
          if (i === 0 || text[i - 1] === ' ' || text[i - 1] === '\n') {
            lastTriggerIndex = i;
            triggerChar = char;
          }
          break;
        }
      }

      if (triggerChar && lastTriggerIndex !== -1) {
        // Extract search query (text after trigger, before cursor)
        const query = text.slice(lastTriggerIndex + 1, from);

        setTriggerIndex(lastTriggerIndex);
        setSearchQuery(query);
        setPopoverType(triggerChar === '#' ? 'session' : 'patient');
      } else {
        // No trigger found, close popover if it was open
        setPopoverType(null);
        setSearchQuery('');
        setTriggerIndex(null);
      }
    };

    // Listen to editor updates
    editor.on('update', handleUpdate);
    editor.on('selectionUpdate', handleUpdate);

    return () => {
      editor.off('update', handleUpdate);
      editor.off('selectionUpdate', handleUpdate);
    };
  }, [editor, enabled, isContextLocked, setPopoverType]);

  // Handle selecting a session - clears any existing patient selection
  const selectSession = useCallback(
    (session: LinkedSessionContext) => {
      if (!editor || triggerIndex === null) return;

      // Replace the trigger text (# + search query) with the session name
      // so the editor reads e.g. "Summarise the history of My Session"
      // triggerIndex is a getText() index (0-based); ProseMirror positions are
      // offset by +1 (paragraph node boundary), so we add 1 to delete from the
      // trigger character itself rather than the character before it.
      const { from } = editor.state.selection;
      editor
        .chain()
        .focus()
        .deleteRange({ from: triggerIndex + 1, to: from })
        .insertContent(session.name)
        .run();

      // Stage the session (single-slot: clears any linked patient).
      stageLinkedSession({ session, origin: linkedContextOriginFor(chatId) });
      setPopoverType(null);
      setSearchQuery('');
      setTriggerIndex(null);
    },
    [editor, triggerIndex, chatId, stageLinkedSession, setPopoverType]
  );

  // Handle selecting a patient - clears any existing session selection
  const selectPatient = useCallback(
    (patient: LinkedPatientContext) => {
      if (!editor || triggerIndex === null) return;

      // Replace the trigger text (@ + search query) with the patient name
      // so the editor reads e.g. "Summarise the history of Dummy Patient"
      // triggerIndex is a getText() index (0-based); ProseMirror positions are
      // offset by +1 (paragraph node boundary), so we add 1 to delete from the
      // trigger character itself rather than the character before it.
      const { from } = editor.state.selection;
      editor
        .chain()
        .focus()
        .deleteRange({ from: triggerIndex + 1, to: from })
        .insertContent(patient.name)
        .run();

      // Stage the patient (single-slot: clears any linked session).
      stageLinkedPatient({ patient, origin: linkedContextOriginFor(chatId) });
      setPopoverType(null);
      setSearchQuery('');
      setTriggerIndex(null);
    },
    [editor, triggerIndex, chatId, stageLinkedPatient, setPopoverType]
  );

  // Close the autocomplete
  const close = useCallback(() => {
    setPopoverType(null);
    setSearchQuery('');
    setTriggerIndex(null);
  }, [setPopoverType]);

  return {
    isOpen,
    type: popoverType,
    searchQuery,
    selectSession,
    selectPatient,
    close,
  };
}
